// Settle — entry point.
// Wires PixiJS renderer, Howler audio, the game engine, input, UI router,
// and the AI Versus controller.

// Self-hosted fonts. Three pixel display faces — Bugvirus (primary),
// CSModleyPixelOutline (H1 billboard titles), CSModleyPixelRegular
// (secondary stats/numbers) — are declared via @font-face in styles.css
// reading from public/fonts/. Hack is the monospace body face and still
// loads from npm so its full latin/latin-ext coverage is bundled.
import 'hack-font/build/web/hack.css';
import './styles.css';

import { HowlerAudioEngine } from './audio-howler/audio';
import { DIFFICULTIES, type BotDifficulty } from './ai/bot';
import { VersusController, getVersusMode } from './ai/versus';
import type { VersusModeId } from './ai/versus';
import { BOARD_BUFFER_HEIGHT, BOARD_WIDTH, TICK_MS } from './config';
import { Engine } from './game/engine';
import { shapeOf } from './game/pieces';
import type { EngineEvent } from './game/engine';
import { Input } from './input/input';
import type { Action } from './input/input';
import { ALL_MODES, formatTime, getMode } from './modes/modes';
import type { ModeRuntime } from './modes/modes';
import { PixiApp } from './render-pixi/app';
import { applyPaletteToCss, getPalette } from './render/palettes';
import { ACHIEVEMENTS, categoryFor, newlyUnlocked } from './state/achievements';
import type { Achievement } from './state/achievements';
import {
  DEFAULT_SETTINGS, DEFAULT_STATS, GUEST_PROFILE_ID,
  adoptLegacyDataIntoProfile, createProfile, deleteProfile, getActiveProfileId, listProfiles,
  hasLocalProgress, loadSettings, loadStats, mergeSaves, saveSettings, saveStats, setActiveProfile,
  setSaveHook, applyCloudSave,
} from './state/storage';
import type { PersistedStats, ProfileRecord, Settings } from './state/storage';
import {
  accountModal, buildAbout, buildAccount, buildChangelog, buildCustomPalettes, buildLeaderboard, buildLegal, buildMenu, buildModes, buildPause, buildProfileSelect, buildResults, buildSettings, buildStats, buildVersusSelect, downloadLocalBackup, reconcileModal, refreshBrandMarks,
} from './ui/screens';
import type { ResultsSubmitCtx } from './ui/screens';
import { getAccount, validateSession } from './net/auth';
import { pullSave, pushNow, schedulePush } from './net/cloudsave';
import { formatLeaderboardValue, leaderboardMetric, submitScore } from './net/leaderboard';
import { ChatWidget } from './net/chat';
import { MiniPlayer } from './ui/miniplayer';
import { CatCompanion } from './ui/cat';
import { FactsCard } from './ui/facts';
import { refreshPresenceDisplays, startPresence } from './net/presence';
import { aboutModal, buildAboutLetter, clearScreen, el, icon, nowPlayingToast, playfieldToast, setWatermarkAction, toast } from './ui/ui';
import { WELCOME_SEEN_KEY } from './about';
import type { GameStats, ModeId, ModeResultDetails } from './types';

class App {
  appRoot: HTMLElement;
  uiHost!: HTMLElement;
  pixiHost!: HTMLElement;
  fpsLabel!: HTMLElement;
  /** DOM overlay for the single viewport-centered countdown / GO message.
   *  Replaces the per-playfield overlays so multi-AI layouts don't render
   *  the same number on every board. */
  countdownOverlay!: HTMLElement;
  /** DOM overlay for end-of-match messages (topped out / finish / victory).
   *  Sits above the playfield so it doesn't overlap the board contents the
   *  way the previous in-board dramaticOverlay did. */
  endOverlay!: HTMLElement;
  /** DOM 'settle' wordmark pinned above the hold box during gameplay.
   *  Inherits the same gradient + shimmer treatment as the menu brand
   *  so the in-game header reads as one continuous identity. */
  playfieldBrand!: HTMLElement;
  chat = new ChatWidget({ profanityOn: () => this.settings.chatFilter });
  miniPlayer!: MiniPlayer;
  /** Decorative cat in the dead space under the stats box. */
  cat!: CatCompanion;
  /** Rotating tetris-trivia card in the dead space under the queue. */
  facts = new FactsCard();
  settings: Settings;
  stats: PersistedStats;
  pixi!: PixiApp;
  audio: HowlerAudioEngine;
  input: Input;
  /** Solo-mode engine (null when in Versus). */
  engine: Engine | null = null;
  /** Versus controller (null when in solo). */
  versus: VersusController | null = null;
  state: 'menu' | 'modes' | 'settings' | 'stats' | 'about' | 'game' | 'results' | 'versus-select' | 'profile-select' | 'custom-palettes' | 'leaderboard' | 'changelog' | 'account' | 'legal' = 'menu';
  /** The currently signed-in profile. Set by setActiveProfile before
   *  any settings or stats are touched. */
  profile: ProfileRecord | null = null;
  currentTab: 'video' | 'audio' | 'handling' | 'controls' | 'themes' | 'general' = 'general';
  currentMode: ModeRuntime | null = null;
  inVersus = false;
  versusDifficulty: BotDifficulty['id'] = 'medium';
  versusModeId: VersusModeId = 'duel';
  paused = false;
  /** When set, navigating from settings back to the menu instead returns
   *  to the paused game (with the pause panel re-opened). */
  private settingsReturnToPause = false;
  /** True while the modes screen was opened FROM the results screen —
   *  keeps the results backdrop treatment + back returns to results. */
  private modesFromResults = false;
  /** Tracks how long Esc has been held during a run. After 5 s the run
   *  is force-ended (top-out → results screen). 0 means not held. */
  private escHoldStartedAt = 0;
  private escHoldToastEl: HTMLElement | null = null;
  private escHoldAbandoned = false;
  countdownMsLeft = 0;
  /** Guards the versus end-of-match branch so the deferred endRun + overlay
   *  cleanup fire exactly once per match, not every frame between the
   *  state transition and the scheduled callback. Reset on new run. */
  private versusEndPending = false;
  /** Resume-countdown state. When the user un-pauses with the toggle on,
   *  the panel dismisses but the engine stays paused while this counts
   *  down to 0 — main loop ticks it independently of !paused gating. */
  private resumeCountdownMsLeft = 0;
  private resumeCountdownLastBeep = -1;
  countdownLastBeep = -1;
  tickAcc = 0;
  fpsSamples: number[] = [];
  fpsLast = 0;
  lastModeId: ModeId | null = null;
  lastModeResult: ModeResultDetails | null = null;
  lastFrameAt = 0;
  private bestComboThisRun = 0;
  private bestB2BThisRun = 0;

  constructor() {
    this.appRoot = document.getElementById('app')!;
    // Resolve active profile before reading any per-profile data. If a
    // profile is set, find its record and load that profile's settings +
    // stats. Otherwise we'll show the profile-select screen at boot.
    const activeId = getActiveProfileId();
    if (activeId) {
      this.profile = listProfiles().find((p) => p.id === activeId) ?? null;
      if (!this.profile) {
        // Active id pointed at a deleted profile — wipe the pointer.
        setActiveProfile('');
      }
    }
    this.settings = loadSettings();
    this.stats = loadStats();
    this.audio = new HowlerAudioEngine();
    this.audio.onTrackStart = (track) => {
      // The in-game miniplayer shows the title; the old scrolling
      // ticker is retired.
      this.miniPlayer.setTitle(track.title ?? null);
      this.miniPlayer.setPlayingIcon(true);
    };
    this.input = new Input();
    this.setupDom();
    this.applySettingsToSubsystems();
    this.input.attach(window);
    this.input.setHandler((a, p, r, e) => this.handleAction(a, p, r, e));
    this.attachWindowEvents();
    void this.bootPixi();
  }

  /** Switch the app over to the given profile. Loads its settings + stats,
   *  re-applies every subsystem, and routes to the main menu. If `isNew`
   *  is true and there's no per-profile data yet, we also adopt any
   *  pre-profile (legacy) localStorage as a one-time migration so a
   *  returning player doesn't lose their existing records. */
  private activateProfile(rec: ProfileRecord, isNew: boolean): void {
    if (isNew && !rec.isGuest && listProfiles().length === 1) {
      // First non-guest profile created on this browser — pull legacy data over.
      adoptLegacyDataIntoProfile(rec.id);
    }
    setActiveProfile(rec.id);
    this.profile = rec;
    // Reload settings + stats from the new profile's namespace. Guest
    // sessions get the defaults since loadSettings/loadStats no-op on
    // the guest sentinel id.
    this.settings = loadSettings();
    this.stats = loadStats();
    this.applySettingsToSubsystems();
    applyPaletteToCss(getPalette(this.settings.paletteId, this.settings.customPalettes));
    this.pixi?.setPalette(getPalette(this.settings.paletteId, this.settings.customPalettes));
    this.pixi?.setPostFxSettings(this.settings.postfx);
    this.pixi?.setRenderOptions(this.settings.render);
    this.pixi?.setRenderScale(this.settings.renderScale);
    this.goto('menu');
  }

  private async bootPixi(): Promise<void> {
    // Push the saved palette's accents to CSS custom properties immediately
    // so the boot loader + first-render menu inherit the right theme.
    applyPaletteToCss(getPalette(this.settings.paletteId, this.settings.customPalettes));
    this.pixi = new PixiApp({
      palette: getPalette(this.settings.paletteId, this.settings.customPalettes),
      renderOpts: this.settings.render,
      postFx: this.settings.postfx,
      renderScale: this.settings.renderScale,
    });
    await this.pixi.init(this.pixiHost);
    window.addEventListener('resize', () => this.pixi.resize(window.innerWidth, window.innerHeight));
    // Drive the main loop off Pixi's ticker so render and update are in lockstep.
    this.pixi.app.ticker.add((ticker) => this.onTick(ticker.deltaMS));
    this.fadeOutLoader();
    // Version watermark doubles as the quiet 'what's new' entry point.
    // Ignored mid-run so a stray click can't yank the player out of an
    // active game (the watermark stays visible over the canvas).
    setWatermarkAction(() => {
      if (this.state === 'game') return;
      this.goto('changelog');
    });
    // Validate any stored account session (clears it on 401), adopt
    // the cloud save when one exists, then start the presence
    // heartbeat. Local saves push to the cloud (debounced) from here
    // on whenever the player is signed in.
    await validateSession();
    await this.syncFromCloud();
    this.installSaveHook();
    startPresence();
    // Local saves are implicit now that accounts are the player-facing
    // identity: adopt the most recently played device profile, or
    // create one silently on first launch. The old profile-select
    // screen stays in code but is no longer routed to.
    if (!this.profile) {
      const all = listProfiles().sort((a, b) => (b.lastPlayedAt ?? 0) - (a.lastPlayedAt ?? 0));
      const rec = all[0] ?? createProfile('player');
      this.activateProfile(rec, false);
    } else {
      this.goto('menu');
    }
  }

  private setupDom(): void {
    this.pixiHost = el('div', { id: 'pixi-host', style: 'position:absolute;inset:0;z-index:1;' });
    this.appRoot.appendChild(this.pixiHost);
    this.fpsLabel = el('div', { id: 'fps' }, ['']);
    this.appRoot.appendChild(this.fpsLabel);
    this.countdownOverlay = el('div', { id: 'countdown-overlay' }, ['']);
    this.appRoot.appendChild(this.countdownOverlay);
    // 'settle' wordmark above the hold box during gameplay. Positioned
    // per-frame in onTick using the playfield's viewport rect. Visible
    // only when #app has the in-game class.
    this.playfieldBrand = el('div', { id: 'playfield-brand', class: 'brand-name' }, ['settle']);
    this.appRoot.appendChild(this.playfieldBrand);
    this.appRoot.appendChild(this.chat.root);
    this.miniPlayer = new MiniPlayer({
      onPrev: () => this.audio.skipPrev(),
      onNext: () => this.audio.skipNext(),
      onToggle: () => this.audio.toggleRadioPause(),
      isPlaying: () => this.audio.radioPlaying(),
      getVolume: () => this.settings.audio.music,
      setVolume: (v) => {
        this.settings.audio.music = v;
        saveSettings(this.settings);
        this.audio.setSettings(this.settings.audio);
      },
      getAnalyser: () => this.audio.musicAnalyser(),
    });
    this.appRoot.appendChild(this.miniPlayer.root);
    this.cat = new CatCompanion(this.audio);
    this.appRoot.appendChild(this.cat.root);
    this.appRoot.appendChild(this.facts.root);
    this.endOverlay = el('div', { id: 'end-overlay' }, ['']);
    this.appRoot.appendChild(this.endOverlay);
    // Two pre-rendered icons stacked inside the button — CSS swaps opacity
    // between them based on the paused class so the morph reads cleanly
    // without re-mounting DOM mid-interaction.
    const burger = el('button', { id: 'menu-button', 'aria-label': 'pause / menu' }, [
      el('span', { class: 'mb-icon mb-icon-open' }, [icon('menu-burger')]),
      el('span', { class: 'mb-icon mb-icon-close' }, [icon('angle-right-solid')]),
    ]);
    burger.addEventListener('click', () => this.onBurgerClick());
    this.appRoot.appendChild(burger);
    this.uiHost = el('div', { class: 'screen' });
    this.appRoot.appendChild(this.uiHost);
    // Fixed bottom-left legal links — the quiet counterpart to the
    // version watermark bottom-right. Ignored mid-run like the
    // watermark so a stray click can't interrupt a game.
    const mkLegal = (label: string, tab: 'terms' | 'privacy') => {
      const b = el('button', { class: 'legal-link', type: 'button' }, [label]);
      b.addEventListener('click', () => {
        if (this.state === 'game') return;
        this.goto('legal', { legalTab: tab });
      });
      return b;
    };
    // 'about' opens the developer letter (same one first-time visitors
    // see over the loader); terms/privacy route to the legal screen.
    const aboutLink = el('button', { class: 'legal-link', type: 'button' }, ['about']);
    aboutLink.addEventListener('click', () => {
      if (this.state === 'game') return;
      aboutModal();
    });
    this.appRoot.appendChild(el('div', { id: 'legal-links' }, [
      aboutLink,
      el('span', { class: 'legal-sep' }, ['·']),
      mkLegal('terms', 'terms'),
      el('span', { class: 'legal-sep' }, ['·']),
      mkLegal('privacy', 'privacy'),
    ]));
  }

  private applySettingsToSubsystems(): void {
    this.audio.setSettings(this.settings.audio);
    this.input.setKeymap(this.settings.keymap);
  }

  private fadeOutLoader(): void {
    const loader = document.getElementById('loader');
    if (!loader) return;
    // Browsers gate audio behind a user gesture, so we explicitly
    // capture one here: the loader stays on screen with a 'click to
    // begin' prompt until the user clicks or presses any key. That
    // gesture initializes the AudioContext, starts the radio (if
    // enabled), then fades the loader out and reveals the menu.
    //
    // First launch on this device swaps the bare prompt for the
    // developer's welcome letter, with 'click to begin' beneath it.
    // The seen-flag is set on dismiss, so every later boot goes
    // straight to the regular loading animation. The letter stays
    // re-readable via the 'about' link in the bottom-left corner.
    let firstVisit = false;
    try { firstVisit = localStorage.getItem(WELCOME_SEEN_KEY) !== '1'; } catch { /* */ }
    if (firstVisit) {
      loader.classList.add('welcome');
      loader.appendChild(el('div', { class: 'welcome-wrap' }, [
        el('div', { class: 'brand-name welcome-brand' }, ['settle']),
        el('div', { class: 'welcome-card' }, [buildAboutLetter()]),
        el('div', { class: 'welcome-begin' }, ['click to begin']),
      ]));
    } else {
      const sub = loader.querySelector('.loader-sub') as HTMLElement | null;
      if (sub) sub.textContent = 'click to begin';
    }
    loader.classList.add('ready');
    const dismiss = () => {
      loader.removeEventListener('pointerdown', dismiss);
      window.removeEventListener('keydown', dismiss);
      try { localStorage.setItem(WELCOME_SEEN_KEY, '1'); } catch { /* */ }
      this.audio.init();
      if (this.settings.radio.enabled) this.audio.startRadio();
      loader.classList.add('hide');
      setTimeout(() => loader.remove(), 500);
    };
    loader.addEventListener('pointerdown', dismiss);
    window.addEventListener('keydown', dismiss);
  }

  private attachWindowEvents(): void {
    document.addEventListener('contextmenu', (e) => e.preventDefault());
    const kick = () => { this.audio.init(); window.removeEventListener('pointerdown', kick); window.removeEventListener('keydown', kick); };
    window.addEventListener('pointerdown', kick);
    window.addEventListener('keydown', kick);
  }

  // ---------- Routing ----------
  private goto(
    state: 'menu' | 'modes' | 'settings' | 'stats' | 'about' | 'game' | 'results' | 'versus-select' | 'profile-select' | 'custom-palettes' | 'leaderboard' | 'changelog' | 'account' | 'legal',
    opts?: {
      tab?: 'video' | 'audio' | 'handling' | 'controls' | 'themes' | 'general';
      legalTab?: 'terms' | 'privacy';
      mode?: ModeId;
      result?: ModeResultDetails;
    },
  ): void {
    this.state = state;
    if (state === 'game') {
      clearScreen(this.uiHost);
      this.uiHost.style.display = 'none';
      this.appRoot.classList.add('in-game');
      this.chat.show();
      this.miniPlayer.show();
      this.miniPlayer.setTitle(this.audio.currentTrackTitle());
      this.cat.show();
      this.facts.show();
    } else {
      this.uiHost.style.display = '';
      this.appRoot.classList.remove('in-game');
      // Chat lives under the queue, so it follows the playfield's
      // visibility (keeps polling while paused sub-screens are up).
      if (!this.appRoot.classList.contains('in-pause-flow')) {
        this.chat.hide();
        this.miniPlayer.hide();
        this.cat.hide();
        this.facts.hide();
      }
    }
    // Tag the results screen specifically so the .screen wash can apply
    // the same backdrop-blur it uses for pause — keeps the visual rhythm
    // consistent (panel up = playfield blurred behind it).
    this.appRoot.classList.toggle('in-results',
      state === 'results' || (state === 'modes' && this.modesFromResults));
    // Freshly-built screens may carry .settlers-online-count nodes —
    // paint them with the latest heartbeat value immediately instead
    // of waiting for the next 45s tick.
    queueMicrotask(() => refreshPresenceDisplays());
    if (state === 'profile-select') {
      // Profile-select screen — shown on first boot (no active profile)
      // or when the user explicitly chooses to switch profile.
      this.pixi?.fadeAndClearPlayfields(300);
      this.appRoot.classList.remove('paused');
      buildProfileSelect(this.host(), {
        profiles: listProfiles(),
        activeId: this.profile?.id ?? null,
        onCreate: (name) => this.activateProfile(createProfile(name), /* isNew */ true),
        onChoose: (id) => {
          const rec = listProfiles().find((p) => p.id === id);
          if (rec) this.activateProfile(rec, false);
        },
        onDelete: (id) => {
          deleteProfile(id);
          if (this.profile?.id === id) this.profile = null;
          this.goto('profile-select');
        },
        onGuest: () => {
          // Sentinel guest profile — never persisted. activateProfile
          // sees the GUEST_PROFILE_ID and routes settings/stats reads
          // and writes through the in-memory copy only.
          this.activateProfile({
            id: GUEST_PROFILE_ID,
            name: 'guest',
            createdAt: Date.now(),
            lastPlayedAt: Date.now(),
            isGuest: true,
          }, false);
        },
      });
    } else if (state === 'menu') {
      // Main menu: only the procedural backdrop should be visible.
      // Fade the play layer out, then collapse multi-AI layouts back to
      // single so leftover royal/triple boards don't ghost behind the menu.
      this.pixi?.fadeAndClearPlayfields(300);
      // Also clear any persistent end-of-match message so it doesn't
      // hang over the main-menu backdrop.
      this.hideEndOverlay();
      this.engine = null;
      this.versus = null;
      this.inVersus = false;
      this.paused = false;
      this.modesFromResults = false;
      this.appRoot.classList.remove('paused');
      // Slow ramp back to full bandwidth — same shape as un-pause, so the
      // 'panel up → menu' transition feels musically continuous instead of
      // a hard snap when leaving the results screen.
      this.audio.setMusicDuck(0, 2.0);
      this.audio.setRadioState(this.settings.radio); this.audio.startRadio();
      buildMenu(this.host());
    } else if (state === 'modes') {
      buildModes(this.host(), this.modesFromResults ? 'results-return' : 'menu');
    } else if (state === 'versus-select') {
      buildVersusSelect(this.host(), this.versusDifficulty, this.versusModeId, (d, m) => {
        this.versusDifficulty = d;
        this.versusModeId = m;
        this.playVersus(d, m);
      });
    } else if (state === 'custom-palettes') {
      buildCustomPalettes(this.host(), {
        customs: this.settings.customPalettes ?? [],
        activeId: this.settings.paletteId,
        onSave: (palette) => {
          if (!this.settings.customPalettes) this.settings.customPalettes = [];
          const idx = this.settings.customPalettes.findIndex((p) => p.id === palette.id);
          if (idx >= 0) this.settings.customPalettes[idx] = palette;
          else this.settings.customPalettes.push(palette);
          saveSettings(this.settings);
          this.goto('custom-palettes');
        },
        onActivate: (id) => {
          this.settings.paletteId = id;
          saveSettings(this.settings);
          const p = getPalette(id, this.settings.customPalettes);
          this.pixi?.setPalette(p);
          applyPaletteToCss(p);
          this.goto('settings', { tab: 'themes' });
        },
        onDelete: (id) => {
          this.settings.customPalettes = (this.settings.customPalettes ?? []).filter((p) => p.id !== id);
          // If the currently-active palette was deleted, fall back to Prism.
          if (this.settings.paletteId === id) {
            this.settings.paletteId = 'prism';
            const p = getPalette('prism', this.settings.customPalettes);
            this.pixi?.setPalette(p);
            applyPaletteToCss(p);
          }
          saveSettings(this.settings);
          this.goto('custom-palettes');
        },
      });
    } else if (state === 'settings') {
      if (opts?.tab) this.currentTab = opts.tab;
      buildSettings(this.host(), this.currentTab);
    } else if (state === 'stats') {
      buildStats(this.host());
    } else if (state === 'about') {
      buildAbout(this.host());
    } else if (state === 'leaderboard') {
      buildLeaderboard(this.host());
    } else if (state === 'changelog') {
      buildChangelog(this.host());
    } else if (state === 'account') {
      buildAccount(this.host());
    } else if (state === 'legal') {
      buildLegal(this.host(), opts?.legalTab ?? 'terms');
    } else if (state === 'game') {
      // canvas is always visible
    } else if (state === 'results') {
      // Match the pause-panel ducking shape so the results screen reads as
      // 'panel up = music muffled' just like pause. 90ms drop on the way
      // in; the play-again / change-mode / menu transitions each unduck
      // on their own (playMode / playVersus / goto('menu') call setMusicDuck(0)).
      this.audio.setMusicDuck(1, 0.09);
      if (opts?.mode && opts?.result) {
        this.lastModeId = opts.mode;
        this.lastModeResult = opts.result;
        buildResults(this.host(), opts.mode, opts.result, () => {
          if (this.inVersus) this.playVersus(this.versusDifficulty);
          else if (this.lastModeId) this.playMode(this.lastModeId);
        }, this.currentSubmitCtx());
      }
    }
  }

  private host() {
    return {
      host: this.uiHost,
      navigate: (nav: Parameters<Parameters<typeof buildMenu>[0]['navigate']>[0]) => {
        if (nav.type === 'menu') {
          if (this.settingsReturnToPause) {
            this.settingsReturnToPause = false;
            this.goto('game');
            this.openPause();
            return;
          }
          this.goto('menu');
        }
        else if (nav.type === 'modes') {
          this.modesFromResults = this.state === 'results';
          this.goto('modes');
        }
        else if (nav.type === 'settings') this.goto('settings', { tab: nav.tab });
        else if (nav.type === 'stats') this.goto('stats');
        else if (nav.type === 'about') this.goto('about');
        else if (nav.type === 'play') this.playMode(nav.mode);
        else if (nav.type === 'results') this.goto('results', { mode: nav.mode, result: nav.result });
        else if (nav.type === 'results-return') {
          if (this.lastModeId && this.lastModeResult) {
            this.goto('results', { mode: this.lastModeId, result: this.lastModeResult });
          } else {
            this.goto('menu');
          }
        }
        else if (nav.type === 'versus') this.goto('versus-select');
        else if (nav.type === 'profile-select') this.goto('profile-select');
        else if (nav.type === 'custom-palettes') this.goto('custom-palettes');
        else if (nav.type === 'leaderboard') this.goto('leaderboard');
        else if (nav.type === 'changelog') this.goto('changelog');
        else if (nav.type === 'account') {
          if (getAccount()) this.goto('account');
          else accountModal(this.host(), () => this.refreshAfterAuth());
        }
        else if (nav.type === 'legal') this.goto('legal', { legalTab: nav.tab });
      },
      settings: this.settings,
      saveSettings: () => saveSettings(this.settings),
      stats: this.stats,
      saveStats: () => saveStats(this.stats),
      beginRebind: (action: Action, slotIndex: number) => this.beginRebind(action, slotIndex),
      onPaletteChange: () => {
        const p = getPalette(this.settings.paletteId, this.settings.customPalettes);
        this.pixi?.setPalette(p);
        applyPaletteToCss(p);
        refreshBrandMarks(this.host());
      },
      onAudioChange: () => { this.audio.setSettings(this.settings.audio); },
      onVideoChange: () => {
        this.pixi?.setPostFxSettings(this.settings.postfx);
        this.pixi?.setRenderOptions(this.settings.render);
        this.pixi?.setRenderScale(this.settings.renderScale);
        // Tetrimino style affects the brand-mark cells too — keep them
        // in sync so swapping style updates the mark immediately without
        // requiring a screen rebuild.
        refreshBrandMarks(this.host());
      },
      onHandlingChange: () => {
        if (this.engine) {
          this.engine.cfg.dasMs = this.settings.handling.dasMs;
          this.engine.cfg.arrMs = this.settings.handling.arrMs;
          this.engine.cfg.softDropFactor = this.settings.handling.softDropFactor;
          this.engine.cfg.dcdMs = this.settings.handling.dcdMs;
        }
      },
      onMusicVariantChange: () => {
        this.audio.setRadioState(this.settings.radio);
        if (this.settings.radio.enabled) this.audio.startRadio();
        else this.audio.stopRadio();
      },
      testSfx: (name: import('./audio/audio').SfxName) => this.audio.play(name),
      playMenuSfx: (kind: 'move' | 'select' | 'back') => {
        if (kind === 'move') this.audio.play('menu_move');
        else if (kind === 'select') this.audio.play('menu_select');
        else this.audio.play('menu_back');
      },
      activeProfileName: this.profile?.name ?? null,
      // Guests browse the board anonymously — no id means no 'you'
      // highlighting and no submissions.
      activeProfileId: this.profile && !this.profile.isGuest ? this.profile.id : null,
      lastMode: this.lastModeId,
    };
  }

  // ---------- Mode lifecycle ----------
  private playMode(id: ModeId): void {
    if (this.versus) { this.versus = null; }
    this.inVersus = false;
    this.paused = false;
    this.appRoot.classList.remove('paused');
    // Clear any stale end-of-match message from the previous run + reset
    // the play stage alpha so a mid-fade restart still shows.
    this.hideEndOverlay();
    this.audio.setMusicDuck(0);
    // Snap the play layer back to visible — if the player restarted mid-fade
    // (e.g. quit-to-menu → play-again), the in-flight tween would otherwise
    // leave the new run invisible.
    this.pixi?.resetPlayStageAlpha();
    const mode = getMode(id);
    this.currentMode = mode;
    const modeCfg = mode.engineConfig();
    this.engine = new Engine({
      dasMs: this.settings.handling.dasMs,
      arrMs: this.settings.handling.arrMs,
      softDropFactor: this.settings.handling.softDropFactor,
      dcdMs: this.settings.handling.dcdMs,
      ghostEnabled: this.settings.render.showGhost,
      initialLevel: modeCfg.initialLevel ?? 1,
      preview: 5,
      levelGravity: modeCfg.levelGravity ?? true,
      noTopout: modeCfg.noTopout ?? false,
    });
    this.bestComboThisRun = 0;
    this.bestB2BThisRun = 0;
    this.engine.start(
      { initialLevel: modeCfg.initialLevel ?? 1, noTopout: modeCfg.noTopout ?? false },
      mode.initBoard,
    );
    this.paused = false;
    this.lastModeId = id;
    // 400ms head-room before the countdown actually starts beeping so the
    // menu-select sfx that triggered this run doesn't collide with the
    // first '3' beep. countdownLastBeep seeded at 4 so the >3000ms window
    // (sec=4) doesn't fire a beep — first beep lands when sec drops to 3.
    this.countdownMsLeft = this.settings.countdownEnabled ? 3400 : 0;
    this.countdownLastBeep = 4;
    this.engine.state = this.settings.countdownEnabled ? 'idle' : 'playing';
    this.pixi.setLayoutMode('single');
    this.pixi.attachPlayer(this.engine);
    this.audio.setRadioState(this.settings.radio); this.audio.startRadio();
    this.goto('game');
  }

  private playVersus(difficulty: BotDifficulty['id'], modeId: VersusModeId = 'duel'): void {
    this.engine = null;
    this.versus = new VersusController({ difficulty, modeId });
    this.versusModeId = modeId;
    this.inVersus = true;
    this.versusEndPending = false;
    this.currentMode = null;
    this.paused = false;
    this.appRoot.classList.remove('paused');
    // Clear any stale end-of-match message from the previous match.
    this.hideEndOverlay();
    // Snap the play layer back to visible in case a fade is mid-flight
    // (e.g. quit-to-menu → play-again).
    this.pixi?.resetPlayStageAlpha();
    this.audio.setMusicDuck(0);
    this.lastModeId = null;
    // 400ms grace before first beep — see playMode comment for rationale.
    this.countdownMsLeft = this.settings.countdownEnabled ? 3400 : 0;
    this.countdownLastBeep = 4;
    if (this.settings.countdownEnabled) {
      this.versus.player.state = 'idle';
      for (const slot of this.versus.ais) slot.engine.state = 'idle';
    }
    this.bestComboThisRun = 0;
    this.bestB2BThisRun = 0;
    // Wire battle feedback. The player gets the full solo treatment (clear
    // SFX, shake, row flashes, accolades) — except fatal events (topout /
    // finish), which versus owns via its own state machine to avoid a
    // double endRun call.
    this.versus.onPlayerEvent = (ev) => {
      if (ev.type === 'topout' || ev.type === 'finish') return;
      this.onEngineEvent(ev);
    };
    this.versus.onAiEvent = (ev, aiIndex) => this.onAiEngineEvent(ev, aiIndex);
    this.versus.onAttackSent = (from, to, lines) => {
      // Endpoints look like 'player' or 'ai:<index>'. Resolve both ends
      // for the visual layer, which needs source + target + line count.
      const parseEnd = (e: typeof from) => e === 'player'
        ? { kind: 'player' as const, idx: 0 }
        : { kind: 'ai' as const, idx: parseInt(e.slice(3), 10) || 0 };
      const src = parseEnd(from);
      const dst = parseEnd(to);
      this.pixi?.sendLineAttack(src.kind, dst.kind, lines, src.idx, dst.idx);
      // Only the player's outgoing attacks get the "send" sfx — AI traffic
      // would machine-gun the speakers in royal mode.
      if (src.kind === 'player') this.audio.play('attack_send', lines);
    };
    this.versus.onGarbageReceived = (who, lines) => {
      let f;
      if (who === 'player') f = this.pixi?.playerField;
      else {
        const idx = parseInt(who.slice(3), 10) || 0;
        f = this.pixi?.aiFieldAt(idx);
      }
      f?.flashBoardRim(undefined, 360);
      f?.shake(Math.min(6, 1.5 + lines * 0.6), 220);
      if (who === 'player') this.audio.play('attack_receive', lines);
    };
    // Pick layout based on AI count: 1 = versus, 2 = triple, 3 = royal.
    const layout = this.versus.ais.length === 1 ? 'versus'
                 : this.versus.ais.length === 2 ? 'triple'
                 : 'royal';
    this.pixi.setLayoutMode(layout);
    this.pixi.attachPlayer(this.versus.player);
    this.pixi.attachAis(this.versus.ais.map((s) => s.engine));
    this.audio.setRadioState(this.settings.radio); this.audio.startRadio();
    this.goto('game');
  }

  /** Light-weight feedback for AI-side events in versus — flashes + a small
   *  shake on the opponent board so the player can see what's happening
   *  there without the AI's effects dominating the screen. */
  private onAiEngineEvent(ev: EngineEvent, aiIndex = 0): void {
    const af = this.pixi?.aiFieldAt(aiIndex);
    if (!af) return;
    if (ev.type === 'lock' && ev.result.cleared.length > 0) {
      af.flashRows(ev.result.cleared);
      let shake = 1 + ev.result.cleared.length * 0.8;
      if (ev.result.tSpinKind === 'full') shake += 1.5;
      if (ev.result.isPerfectClear) shake += 3;
      af.shake(shake, 220);
    } else if (ev.type === 'harddrop') {
      af.shake(Math.min(4, 0.5 + ev.cells * 0.18), 180);
    } else if (ev.type === 'topout') {
      af.dramaticOverlay('topped out', 'topout');
    }
  }

  private endRun(reason: 'finish' | 'topout' | 'versus-win' | 'versus-lose'): void {
    const engine = this.activeEngine();
    if (!engine) return;
    const stats = engine.stats;
    stats.elapsedMs = performance.now() - stats.startTime;
    this.stats.totalRuns += 1;
    this.stats.totalLines += stats.lines;
    this.stats.totalPieces += stats.pieces;
    this.stats.totalTetrises += stats.tetrises;
    this.stats.totalTspins += stats.tspins;
    this.stats.totalTspinsFull += stats.tspinsFull;
    this.stats.totalTspinsMini += stats.tspinsMini;
    this.stats.totalTspinSingles += stats.tspinSingles;
    this.stats.totalTspinDoubles += stats.tspinDoubles;
    this.stats.totalTspinTriples += stats.tspinTriples;
    this.stats.totalPerfectClears += stats.perfectClears;
    this.stats.totalHardDrops += stats.hardDrops;
    this.stats.totalPlaytimeMs += stats.elapsedMs;
    if (this.currentMode && (reason === 'finish' || (reason === 'topout' && stats.lines > 0))) {
      const id = this.currentMode.id;
      this.stats.modeRuns[id] = (this.stats.modeRuns[id] ?? 0) + 1;
    }
    saveStats(this.stats);

    let result: ModeResultDetails;
    let modeIdForResult: ModeId;
    if (this.inVersus && this.versus) {
      const won = reason === 'versus-win';
      modeIdForResult = 'sprint40';
      // Battle results: primary metric is ALWAYS player lines sent —
      // that's the headline number a versus run pivots on. The
      // win/loss reads in the subtitle line; the four secondary stats
      // are derived play quality (lines cleared, pieces, pps, time).
      result = {
        reasonText: won
          ? `victory vs ${DIFFICULTIES[this.versusDifficulty].name}`
          : `defeat vs ${DIFFICULTIES[this.versusDifficulty].name}`,
        primaryMetric: { label: 'lines sent', value: `${this.versus.playerLinesSent}` },
        secondaryMetrics: [
          { label: 'lines cleared', value: `${stats.lines}` },
          { label: 'pieces', value: `${stats.pieces}` },
          { label: 'pps', value: (stats.pieces / Math.max(1, stats.elapsedMs / 1000)).toFixed(2) },
          { label: 'time', value: formatTime(stats.elapsedMs) },
        ],
        newRecord: false,
      };
    } else if (this.currentMode) {
      const mode = this.currentMode;
      let isPb = false;
      // Capture the *previous* PB before we overwrite it so the results
      // screen can render a "previous best" line + improvement delta.
      const prevBestRaw: number | null = mode.pbKey ? this.stats.pbs[mode.pbKey] ?? null : null;
      const currentRaw: number | null = mode.pbKey ? mode.pbExtract(stats) : null;
      if (mode.pbKey && currentRaw !== null && currentRaw !== Number.POSITIVE_INFINITY) {
        if (prevBestRaw == null) { isPb = true; this.stats.pbs[mode.pbKey] = currentRaw; }
        else if (mode.pbLowerBetter ? currentRaw < prevBestRaw : currentRaw > prevBestRaw) {
          isPb = true; this.stats.pbs[mode.pbKey] = currentRaw;
        }
      }
      saveStats(this.stats);
      result = mode.buildResult(stats, prevBestRaw);
      result.newRecord = isPb;
      result.prevBestRaw = prevBestRaw;
      result.currentRaw = currentRaw === Number.POSITIVE_INFINITY ? null : currentRaw;
      result.pbLowerBetter = mode.pbLowerBetter;
      modeIdForResult = mode.id;
      if (isPb) toast('★ new personal best ★');
    } else {
      modeIdForResult = 'sprint40';
      result = {
        reasonText: 'run ended',
        primaryMetric: { label: 'score', value: stats.score.toLocaleString() },
        secondaryMetrics: [],
      };
    }
    this.lastModeResult = result;
    // Remember the submittable payload (mode + raw value) so the
    // results screen can rebuild its submit button at any time —
    // including right after a sign-in from the results screen itself.
    this.lastSubmit = this.eligibleSubmit(reason, result);
    this.goto('results', { mode: modeIdForResult, result });
  }

  /** Every save path (screens, endRun, achievements) pushes to the cloud
   *  through this hook — re-installed after reconciliation suspends it. */
  private installSaveHook(): void {
    setSaveHook(() => schedulePush(() => ({ settings: this.settings, stats: this.stats })));
  }

  /** Write a save into local storage (no hook fires) and re-apply it to
   *  every live subsystem — the single adoption path used by cloud-wins
   *  sync, merge, and start-fresh. */
  private adoptSave(data: { settings?: unknown; stats?: unknown }): void {
    applyCloudSave(data);
    this.settings = loadSettings();
    this.stats = loadStats();
    this.applySettingsToSubsystems();
    const p = getPalette(this.settings.paletteId, this.settings.customPalettes);
    applyPaletteToCss(p);
    this.pixi?.setPalette(p);
    this.pixi?.setPostFxSettings(this.settings.postfx);
    this.pixi?.setRenderOptions(this.settings.render);
    this.pixi?.setRenderScale(this.settings.renderScale);
    this.miniPlayer?.refresh();
  }

  /** Pull the account's cloud save and reconcile it with this device.
   *
   *  First contact between an account and a device that already has
   *  real progress (pre-account players especially) raises a modal so
   *  the PLAYER decides: merge both, keep one side, or start fresh —
   *  instead of cloud silently overwriting local (the data-loss clash).
   *  The decision is remembered per account+browser
   *  ('settle:reconciled:<username>'); afterwards sync is silent again:
   *  cloud wins when present, otherwise local seeds the cloud. */
  private async syncFromCloud(): Promise<void> {
    const acct = getAccount();
    if (!acct) return;
    const res = await pullSave();
    if (!res) return;  // offline / unconfigured — keep local, ask next time
    const cloudHasData = !!(res.data && typeof res.data === 'object');
    const reconcileKey = `settle:reconciled:${acct.username.toLowerCase()}`;
    let reconciled = false;
    try { reconciled = localStorage.getItem(reconcileKey) === '1'; } catch { /* */ }
    const markReconciled = () => { try { localStorage.setItem(reconcileKey, '1'); } catch { /* */ } };

    if (!reconciled && hasLocalProgress(this.stats, this.settings)) {
      // Suspend cloud pushes while the modal is up so no save path can
      // race the decision and overwrite either side.
      setSaveHook(null);
      await reconcileModal(this.host(), {
        username: acct.username,
        cloudHasData,
        exportLocal: () => downloadLocalBackup(this.settings, this.stats),
        onChoose: async (choice) => {
          if (choice === 'merge' && res.data) {
            const lowerBetter = new Set(
              ALL_MODES.filter((m) => m.pbKey && m.pbLowerBetter).map((m) => m.pbKey!),
            );
            const merged = mergeSaves(this.settings, this.stats, res.data, lowerBetter);
            this.adoptSave(merged);
            await pushNow({ settings: this.settings, stats: this.stats });
            toast('progress merged into your account');
          } else if (choice === 'cloud' && res.data) {
            this.adoptSave(res.data as { settings?: unknown; stats?: unknown });
            toast('cloud save adopted on this device');
          } else if (choice === 'local') {
            await pushNow({ settings: this.settings, stats: this.stats });
            toast('progress moved into your account');
          } else if (choice === 'fresh') {
            this.adoptSave({
              settings: structuredClone(DEFAULT_SETTINGS),
              stats: structuredClone(DEFAULT_STATS),
            });
            await pushNow({ settings: this.settings, stats: this.stats });
            toast('fresh start — good luck out there');
          }
          markReconciled();
        },
      });
      this.installSaveHook();
      return;
    }

    // Reconciled (or nothing local worth asking about) — silent sync.
    if (cloudHasData) {
      this.adoptSave(res.data as { settings?: unknown; stats?: unknown });
    } else {
      // First sign-in from this account: seed the cloud with local.
      void pushNow({ settings: this.settings, stats: this.stats });
    }
    markReconciled();
  }

  /** Rebuild whatever screen is behind the account modal so the new
   *  signed-in state shows immediately (menu chip, results submit). */
  private refreshAfterAuth(): void {
    void this.syncFromCloud().then(() => {
      if (this.state === 'results' && this.lastModeId && this.lastModeResult) {
        this.goto('results', { mode: this.lastModeId, result: this.lastModeResult });
      } else if (this.state === 'menu') {
        this.goto('menu');
      } else {
        this.goto('account');
      }
    });
  }

  /** The submittable payload for the run that just ended — or null
   *  when it doesn't qualify. Rules: solo only (no versus), mode must
   *  be on the board (zen never is), and time-ranked modes (sprint /
   *  garbage) only count when the objective was actually completed —
   *  a topout mid-dig has no meaningful time to rank. */
  private lastSubmit: { mode: ModeId; value: number } | null = null;
  private eligibleSubmit(
    reason: 'finish' | 'topout' | 'versus-win' | 'versus-lose',
    result: ModeResultDetails,
  ): { mode: ModeId; value: number } | null {
    if (this.inVersus || !this.currentMode) return null;
    const mode = this.currentMode;
    const metric = leaderboardMetric(mode.id);
    if (!metric) return null;
    if (metric === 'time' && reason !== 'finish') return null;
    const value = result.currentRaw;
    if (value == null || !(value > 0)) return null;
    return { mode: mode.id, value };
  }

  /** Live submit context derived from lastSubmit at render time, so
   *  signedOut always reflects the CURRENT session state. */
  private currentSubmitCtx(): ResultsSubmitCtx | undefined {
    const ls = this.lastSubmit;
    if (!ls) return undefined;
    return {
      valueText: formatLeaderboardValue(ls.mode, ls.value),
      doSubmit: () => submitScore(ls),
      signedOut: !getAccount(),
    };
  }

  private activeEngine(): Engine | null {
    if (this.versus) return this.versus.player;
    return this.engine;
  }

  // ---------- Input ----------
  private handleAction(action: Action, pressed: boolean, repeat: boolean, _e: KeyboardEvent): void {
    if (this.state !== 'game' && this.state !== 'results') return;
    if (this.state === 'game') {
      const isCountdown = this.countdownMsLeft > 0;
      // Hold-escape-to-abandon: a sustained 5s hold during a run forces
      // an end-of-run regardless of pause state. The handler tracks the
      // first-down timestamp and a short, repeating progress toast.
      if (action === 'pause') {
        if (pressed && !repeat) {
          this.escHoldStartedAt = performance.now();
          this.escHoldAbandoned = false;
          if (isCountdown) return;
          // Block pause toggle while the resume countdown is in flight
          // — otherwise tapping escape during 3-2-1 would just reset the
          // timer to 3 over and over.
          if (this.resumeCountdownMsLeft > 0) return;
          if (!this.paused) this.openPause(); else this.closePause();
          return;
        } else if (!pressed) {
          this.escHoldStartedAt = 0;
          if (this.escHoldToastEl) { this.escHoldToastEl.remove(); this.escHoldToastEl = null; }
        }
        return;
      }
      if (action === 'restart' && pressed && !repeat) {
        if (this.inVersus) this.playVersus(this.versusDifficulty);
        else if (this.lastModeId) this.playMode(this.lastModeId);
        return;
      }
      if (this.paused || isCountdown) return;
      const eng = this.activeEngine();
      if (!eng) return;
      if (action === 'left') eng.setInput({ left: pressed }, performance.now());
      else if (action === 'right') eng.setInput({ right: pressed }, performance.now());
      else if (action === 'softDrop') {
        // Guard against key-repeat re-firing the input + audio every ~30 ms.
        if (!repeat) {
          eng.setInput({ softDrop: pressed }, performance.now());
          if (pressed) this.audio.play('softdrop');
        }
      }
      else if (action === 'hardDrop' && pressed && !repeat) {
        // Hard-drop guard: optionally suppress slams within N ms of a
        // fresh piece spawn so a held space bar doesn't carry over.
        const guard = this.settings.handling.hardDropGuardMs;
        if (guard > 0 && eng.piece && performance.now() - eng.pieceSpawnedAt < guard) {
          /* swallow this input */
        } else if (eng.piece) { eng.hardDrop(); this.audio.play('harddrop'); }
      }
      else if (action === 'rotateCW' && pressed && !repeat) { eng.rotate('cw'); this.audio.play('rotate'); }
      else if (action === 'rotateCCW' && pressed && !repeat) { eng.rotate('ccw'); this.audio.play('rotate'); }
      else if (action === 'rotate180' && pressed && !repeat) { eng.rotate('180'); this.audio.play('rotate'); }
      else if (action === 'hold' && pressed && !repeat) {
        if (eng.piece && !eng.holdLocked) { eng.hold(); this.audio.play('hold'); }
      }
      return;
    }
    if (this.state === 'results') {
      if (action === 'restart' && pressed && !repeat) {
        if (this.inVersus) this.playVersus(this.versusDifficulty);
        else if (this.lastModeId) this.playMode(this.lastModeId);
      }
      if (action === 'pause' && pressed && !repeat) this.goto('menu');
    }
  }

  private beginRebind(action: Action, slotIndex: number): void {
    const btns = document.querySelectorAll<HTMLButtonElement>('.keybind-btn');
    btns.forEach((b) => {
      const a = b.dataset.action as Action; const slot = parseInt(b.dataset.slot ?? '-1', 10);
      if (a === action && slot === slotIndex) b.classList.add('listening');
    });
    this.input.captureNext((code) => {
      const codes = [...(this.settings.keymap[action] ?? [])];
      while (codes.length <= slotIndex) codes.push('');
      codes[slotIndex] = code;
      this.settings.keymap[action] = codes.filter((c, i, a) => c && a.indexOf(c) === i);
      saveSettings(this.settings);
      this.input.setKeymap(this.settings.keymap);
      this.goto('settings', { tab: 'controls' });
    });
  }

  // ---------- Pause ----------
  /** Show a progress toast + abandon the run after a sustained 5 s Esc hold. */
  private tickEscHoldToAbandon(now: number): void {
    if (this.state !== 'game' || this.escHoldStartedAt === 0 || this.escHoldAbandoned) return;
    const heldMs = now - this.escHoldStartedAt;
    if (heldMs < 800) return;  // grace period — first 800 ms is for pause-tap intent
    if (!this.escHoldToastEl) {
      this.escHoldToastEl = el('div', { class: 'esc-hold-toast' }, [
        el('div', { class: 'esc-hold-label' }, ['hold esc to abandon run']),
        el('div', { class: 'esc-hold-bar' }, [
          el('div', { class: 'esc-hold-bar-fill' }),
        ]),
      ]);
      document.body.appendChild(this.escHoldToastEl);
    }
    const totalMs = 5000;
    const pct = Math.min(1, (heldMs - 800) / (totalMs - 800));
    const fill = this.escHoldToastEl.querySelector('.esc-hold-bar-fill') as HTMLElement | null;
    if (fill) fill.style.transform = `scaleX(${pct})`;
    if (heldMs >= totalMs) {
      this.escHoldAbandoned = true;
      if (this.escHoldToastEl) { this.escHoldToastEl.remove(); this.escHoldToastEl = null; }
      this.escHoldStartedAt = 0;
      // Force end the run. Pause is implicitly resolved.
      this.paused = false;
      this.appRoot.classList.remove('paused');
      this.appRoot.classList.remove('in-pause-flow');
      // Gradual unduck — snaps would jolt; let the low-pass sweep back open.
      this.audio.setMusicDuck(0, 2.0);
      clearScreen(this.uiHost);
      this.uiHost.style.display = 'none';
      // Trigger a topout event → endRun flow.
      const eng = this.activeEngine();
      if (eng) eng.events.push({ type: 'topout' });
    }
  }

  private onBurgerClick(): void {
    // Block while the resume countdown is in flight — same reasoning as
    // the keyboard pause handler: tapping the burger would reset the
    // 3-2-1 timer instead of cleanly resuming.
    if (this.state === 'game' && this.resumeCountdownMsLeft > 0) return;
    if (this.state === 'game' && !this.paused) this.openPause();
    else if (this.state === 'game' && this.paused) this.closePause();
    else this.goto('menu');
  }

  private openPause(): void {
    // Side effects (pauseTimer, audio, classes) only fire on the
    // transition INTO paused. If we're already paused — e.g., coming
    // back from settings via the settingsReturnToPause flow — skip them
    // and just rebuild the panel UI. Without this rebuild step the
    // panel destroyed by goto('settings') would never come back, and
    // the user would be left with the game canvas plus no controls.
    if (!this.paused) {
      this.paused = true;
      this.appRoot.classList.add('paused');
      this.appRoot.classList.add('in-pause-flow');
      // Halt the engine's wall-clock timer so timed modes (sprint / ultra)
      // don't drain elapsed time while paused.
      this.engine?.pauseTimer();
      if (this.versus) {
        this.versus.player.pauseTimer();
        for (const slot of this.versus.ais) slot.engine.pauseTimer();
      }
      this.audio.play('menu_back');
      // Quick muffle on pause-open (90ms — feels snappy / immediate). The
      // matching unduck on resume uses a much slower ramp (2.0s) so the
      // music sweeps gently back open.
      this.audio.setMusicDuck(1, 0.09);
    } else {
      // Re-entering pause from a sub-screen — re-apply the classes in
      // case an intermediate goto() removed them, but don't re-pause
      // the timer (already paused) or replay the open sfx.
      this.appRoot.classList.add('paused');
      this.appRoot.classList.add('in-pause-flow');
    }
    const eng = this.activeEngine();
    if (!eng) return;
    this.uiHost.style.display = '';
    buildPause(this.host(), {
      onResume: () => this.closePause(),
      onRestart: () => { if (this.inVersus) this.playVersus(this.versusDifficulty); else if (this.lastModeId) this.playMode(this.lastModeId); },
      onSettings: () => {
        // Keep paused state — settings is a sub-screen, not a quit.
        // Music stays ducked so the menu mood is consistent.
        this.settingsReturnToPause = true;
        this.goto('settings');
      },
      onQuit: () => {
        this.paused = false;
        this.appRoot.classList.remove('paused');
        this.appRoot.classList.remove('in-pause-flow');
        this.audio.setMusicDuck(0, 2.0);
        // Count meaningful Zen / abandoned runs toward lifetime stats and
        // the "completed every mode" achievement. We treat any run with
        // at least ten pieces placed as a valid play session.
        if (!this.inVersus && this.currentMode && eng.stats.pieces >= 10) {
          eng.stats.elapsedMs = performance.now() - eng.stats.startTime;
          this.stats.totalRuns += 1;
          this.stats.totalLines += eng.stats.lines;
          this.stats.totalPieces += eng.stats.pieces;
          this.stats.totalTetrises += eng.stats.tetrises;
          this.stats.totalTspins += eng.stats.tspins;
          this.stats.totalTspinsFull += eng.stats.tspinsFull;
          this.stats.totalTspinsMini += eng.stats.tspinsMini;
          this.stats.totalTspinSingles += eng.stats.tspinSingles;
          this.stats.totalTspinDoubles += eng.stats.tspinDoubles;
          this.stats.totalTspinTriples += eng.stats.tspinTriples;
          this.stats.totalPerfectClears += eng.stats.perfectClears;
          this.stats.totalHardDrops += eng.stats.hardDrops;
          this.stats.totalPlaytimeMs += eng.stats.elapsedMs;
          const id = this.currentMode.id;
          this.stats.modeRuns[id] = (this.stats.modeRuns[id] ?? 0) + 1;
          saveStats(this.stats);
        }
        this.engine?.stop();
        this.versus = null;
        this.inVersus = false;
        // goto('menu') runs fadeAndClearPlayfields which detaches every
        // engine + collapses the layout — no explicit attach(null) needed.
        this.goto('menu');
      },
      modeLabel: this.currentMode
        ? this.currentMode.name
        : this.inVersus
          ? `${getVersusMode(this.versusModeId).name} · ${DIFFICULTIES[this.versusDifficulty].name}`
          : undefined,
      stats: {
        score: eng.stats.score, lines: eng.stats.lines, pieces: eng.stats.pieces, level: eng.stats.level,
        pps: eng.stats.pieces / Math.max(1, eng.stats.elapsedMs / 1000),
        elapsedMs: eng.stats.elapsedMs,
      },
      pbContext: this.pbContextForPause(),
    });
  }

  /** Build the pause-panel PB context: the current mode's PB (if any) and
   *  whether the current run is on track to beat it. Used by buildPause
   *  to render a small "best to beat" line so the player knows what
   *  they're chasing. */
  private pbContextForPause(): { label: string; target: string; status: 'ahead' | 'behind' | 'baseline' | null } | undefined {
    if (this.inVersus) return undefined;
    const eng = this.activeEngine();
    if (!this.currentMode || !this.currentMode.pbKey || !eng) return undefined;
    const mode = this.currentMode;
    const prev = this.stats.pbs[mode.pbKey!];
    if (prev == null) {
      return { label: 'no personal best yet', target: '', status: null };
    }
    const cur = mode.pbExtract(eng.stats);
    const lower = mode.pbLowerBetter;
    const targetStr = lower ? formatTime(prev) : prev.toLocaleString();
    let status: 'ahead' | 'behind' | null = null;
    if (cur !== Number.POSITIVE_INFINITY) {
      status = (lower ? cur < prev : cur > prev) ? 'ahead' : 'behind';
    }
    return { label: 'personal best to beat', target: targetStr, status };
  }

  private closePause(): void {
    if (!this.paused) return;
    this.audio.play('menu_select');
    // Gentle ramp back to full bandwidth so the music doesn't snap when
    // the player un-pauses — matches the feel of the pause-side duck.
    this.audio.setMusicDuck(0, 2.0);
    clearScreen(this.uiHost);
    this.uiHost.style.display = 'none';
    if (this.settings.resumeCountdownEnabled) {
      // Tear down the visual pause state (panel + blur) immediately, but
      // keep this.paused true so engine ticks stay gated while the
      // resume countdown plays its overlay. Main loop drains it.
      this.appRoot.classList.remove('paused');
      this.appRoot.classList.remove('in-pause-flow');
      this.resumeCountdownMsLeft = 3400;  // 400ms grace + 3-2-1
      this.resumeCountdownLastBeep = 4;
      return;
    }
    this.paused = false;
    this.appRoot.classList.remove('paused');
    this.appRoot.classList.remove('in-pause-flow');
    // Resume the wall-clock timer immediately when no countdown is set.
    this.resumeEngineTimers();
  }

  /** Shift every active engine's startTime forward by the paused duration
   *  so elapsed time picks up where it left off. Called once paused
   *  transitions to false — either immediately from closePause (when the
   *  resume countdown is disabled) or after the countdown drains. */
  private resumeEngineTimers(): void {
    this.engine?.resumeTimer();
    if (this.versus) {
      this.versus.player.resumeTimer();
      for (const slot of this.versus.ais) slot.engine.resumeTimer();
    }
  }

  /** Show the viewport-centered countdown overlay using the CS Modley
   *  Pixel hero font. Plain DOM text + a CSS keyframe — simpler + more
   *  readable than the tetrimino-cell rendering, which the user felt
   *  read like post-it notes. */
  private showCountdownText(text: string, kind: 'count' | 'go' = 'count'): void {
    this.countdownOverlay.className = '';
    this.countdownOverlay.textContent = text;
    void this.countdownOverlay.offsetWidth;
    this.countdownOverlay.className = `visible ${kind}`;
  }

  private hideCountdownText(): void {
    this.countdownOverlay.className = '';
    this.countdownOverlay.textContent = '';
  }

  /** Show the end-of-match message above the playfield. Plain text in
   *  the brand title font — pops in, glides upward to rest just above
   *  the board, and stays there until the next run starts (clearEndOverlay).
   *  Sits at a low z so the pause-screen backdrop-filter blurs it
   *  alongside the rest of the playing surface. */
  private showEndOverlay(text: string, kind: 'topout' | 'finish' | 'victory'): void {
    this.endOverlay.className = '';
    this.endOverlay.textContent = text;
    void this.endOverlay.offsetWidth;
    this.endOverlay.className = `visible kind-${kind}`;
  }
  private hideEndOverlay(): void {
    this.endOverlay.className = '';
    this.endOverlay.textContent = '';
  }

  /** Per-tick handler for the resume-countdown overlay. Runs while paused
   *  is true but resumeCountdownMsLeft > 0; on hit-zero, flips paused off
   *  and shows GO. */
  private tickResumeCountdown(dt: number): void {
    if (this.resumeCountdownMsLeft <= 0) return;
    this.resumeCountdownMsLeft -= dt;
    const sec = Math.ceil(this.resumeCountdownMsLeft / 1000);
    if (sec !== this.resumeCountdownLastBeep && sec > 0) {
      this.resumeCountdownLastBeep = sec;
      this.audio.play('countdown');
      this.showCountdownText(String(sec), 'count');
    }
    if (this.resumeCountdownMsLeft <= 0) {
      this.paused = false;
      this.resumeEngineTimers();
      this.audio.play('go');
      this.showCountdownText('GO', 'go');
      setTimeout(() => this.hideCountdownText(), 480);
    }
  }

  // ---------- Main loop ----------
  private onTick(dtMs: number): void {
    const now = performance.now();
    const dt = Math.min(100, dtMs);
    this.fpsSamples.push(1000 / Math.max(0.001, dt));
    if (this.fpsSamples.length > 60) this.fpsSamples.shift();
    if (now - this.fpsLast > 250) {
      this.fpsLast = now;
      const avg = this.fpsSamples.reduce((a, b) => a + b, 0) / this.fpsSamples.length;
      this.fpsLabel.textContent = this.settings.showFps ? `${avg.toFixed(0)} FPS` : '';
    }
    this.tickEscHoldToAbandon(now);
    // Resume countdown ticks even while paused — gates physics on its own
    // resumeCountdownMsLeft counter, flips paused off when it expires.
    if (this.state === 'game' && this.paused && this.resumeCountdownMsLeft > 0) {
      this.tickResumeCountdown(dt);
    }
    if (this.state === 'game' && !this.paused) {
      if (this.countdownMsLeft > 0) {
        this.countdownMsLeft -= dt;
        const sec = Math.ceil(this.countdownMsLeft / 1000);
        if (sec !== this.countdownLastBeep && sec > 0) {
          this.countdownLastBeep = sec;
          this.audio.play('countdown');
          this.showCountdownText(String(sec), 'count');
        }
        if (this.countdownMsLeft <= 0) {
          if (this.versus) {
            const now = performance.now();
            this.versus.player.state = 'playing';
            this.versus.player.stats.startTime = now;
            // Flip every AI slot to 'playing' — previously only ais[0]
            // (the back-compat alias) got toggled, which left ais[1] and
            // ais[2] stuck in 'idle' in triple / royal modes.
            for (const slot of this.versus.ais) {
              slot.engine.state = 'playing';
              slot.engine.stats.startTime = now;
            }
          } else if (this.engine) {
            this.engine.state = 'playing';
            this.engine.stats.startTime = performance.now();
          }
          this.audio.play('go');
          this.showCountdownText('GO', 'go');
          setTimeout(() => this.hideCountdownText(), 480);
        }
      } else {
        // Fixed-timestep engine updates so handling is identical at any FPS.
        this.tickAcc += dt;
        let steps = 0;
        while (this.tickAcc >= TICK_MS && steps < 8) {
          this.tickAcc -= TICK_MS;
          steps++;
          if (this.versus) {
            this.versus.update(TICK_MS);
          } else if (this.engine) {
            this.engine.update(TICK_MS);
            while (this.engine.events.length) {
              this.onEngineEvent(this.engine.events.shift()!);
            }
            if (this.currentMode && this.engine.state === 'playing' && this.currentMode.shouldFinish(this.engine)) {
              this.engine.finish();
            }
            this.engine.stats.elapsedMs = performance.now() - this.engine.stats.startTime;
          }
        }
        // Versus win/lose check
        if (this.versus && this.versus.state !== 'running' && !this.versusEndPending) {
          this.versusEndPending = true;
          const reason: 'versus-win' | 'versus-lose' = this.versus.state === 'player-won' ? 'versus-win' : 'versus-lose';
          if (reason === 'versus-win') this.audio.play('pc'); else this.audio.play('topout');
          this.showEndOverlay(reason === 'versus-win' ? 'victory' : 'defeat', reason === 'versus-win' ? 'victory' : 'topout');
          setTimeout(() => this.endRun(reason), 1100);
        }
      }
    }
    // Drive Pixi animations.
    if (this.pixi) {
      this.pixi.update(dt);
      // Re-anchor the 'settle' wordmark above the hold box. Cheap to
      // run per frame; covers resize + layout-mode switches without
      // a separate notification path. Hidden when not in-game.
      const inGame = this.state === 'game';
      // The playfield is also rendered behind any pause sub-screen
      // (settings / themes / etc. reached from pause). Keep the brand
      // + burger pinned near it across those screens so they don't
      // suddenly fly back to the viewport corner mid-navigation.
      const playfieldVisible = inGame || this.appRoot.classList.contains('in-pause-flow');
      if (playfieldVisible) {
        const rect = this.pixi.playerBoardViewportRect();
        const sidebarLeft = this.pixi.playerSidebarLeft();
        if (rect && sidebarLeft != null) {
          this.playfieldBrand.style.display = inGame ? 'block' : 'none';
          // Burger sits at the hold box's left corner; the wordmark
          // rides just right of it (40px button + 12px gap).
          this.playfieldBrand.style.left = `${sidebarLeft + 52}px`;
          this.playfieldBrand.style.top = `${rect.top - 48}px`;
          const burger = document.getElementById('menu-button');
          if (burger) {
            // Left-aligned with the hold box's left edge.
            burger.style.left = `${Math.max(12, sidebarLeft)}px`;
            burger.style.top = `${Math.max(12, rect.top - 52)}px`;
          }
          const queue = this.pixi.playerQueueViewportRect();
          if (queue) {
            // Every DOM gap matches the Pixi layout's own spacing — read
            // the real board→queue gap off the viewport rects so the
            // chat column never looks tighter than the canvas gutters.
            const gap = Math.max(8, Math.round(queue.left - (rect.left + rect.width)));
            // The miniplayer and chat live together in a single right-edge
            // column, just outside the Pixi queue — both at the
            // comfortable chat width so the column reads as one unit.
            const colLeft = queue.left + queue.width + gap;
            const colWidth = Math.max(240, Math.min(340, window.innerWidth - colLeft - 16));
            const colTop = queue.top;
            const colBottom = queue.boardBottom;
            const mpHeight = 140;  // includes the 22px visualizer strip
            this.miniPlayer.position(colLeft, colTop, colWidth, mpHeight);
            const chatTop = colTop + mpHeight + gap;
            this.chat.position(colLeft, chatTop, colWidth, Math.max(140, colBottom - chatTop));
            // Tell Pixi how wide the DOM column is so the board assembly
            // shifts left and the WHOLE composition (sidebar → chat)
            // centers on the page. Converges in a couple frames since
            // colWidth itself depends on the board position; the setter
            // no-ops once stable.
            this.pixi.setDomGutterRight(gap + colWidth);
            // Dead-space fillers: the cat lounges under the stats box
            // (left sidebar), the trivia card fills under the queue.
            // Both hide themselves when the layout leaves no room.
            const sidebarRight = rect.left - gap;
            const catTop = queue.statsBottom + gap;
            this.cat.position(sidebarLeft, catTop, sidebarRight - sidebarLeft, queue.boardBottom - catTop);
            const factTop = queue.previewsBottom + gap;
            this.facts.position(queue.left, factTop, queue.width, queue.boardBottom - factTop);
          }
        } else {
          this.playfieldBrand.style.display = 'none';
        }
      } else {
        this.playfieldBrand.style.display = 'none';
        const burger = document.getElementById('menu-button');
        if (burger) { burger.style.left = ''; burger.style.top = ''; }
      }
      // Update HUD text in playfields each tick
      if (this.engine && this.pixi.playerField) {
        const mode = this.currentMode;
        const metric = mode ? metricFor(mode.id, this.engine.stats) : { label: 'score', value: this.engine.stats.score.toLocaleString() };
        this.pixi.playerField.setHudData({ modeName: mode?.name ?? 'GAME', metricLabel: metric.label, metricValue: metric.value });
      }
      if (this.versus && this.pixi.playerField) {
        const ps = this.versus.player.stats;
        const incoming = this.versus.pendingGarbageFor('player');
        const metricVal = incoming > 0 ? `+${incoming}` : '0';
        const mode = getVersusMode(this.versusModeId);
        this.pixi.playerField.setHudData({ modeName: mode.name, metricLabel: 'incoming', metricValue: metricVal });
        // Each AI gets its own HUD with its own incoming counter.
        for (let i = 0; i < this.versus.ais.length; i++) {
          const f = this.pixi.aiFieldAt(i);
          if (!f) continue;
          const slot = this.versus.ais[i]!;
          const aiIncoming = slot.garbageQueue;
          const aiMetric = aiIncoming > 0 ? `+${aiIncoming}` : '0';
          // Label distinguishes which AI when multiple are on screen.
          const diffName = DIFFICULTIES[this.versusDifficulty].name;
          const label = this.versus.ais.length === 1
            ? diffName
            : `${diffName} ${i + 1}`;
          f.setHudData({ modeName: label, metricLabel: slot.alive ? 'incoming' : 'topped out', metricValue: slot.alive ? aiMetric : '×' });
        }
        void ps;
      }
    }
    this.lastFrameAt = now;
  }

  // ---------- Engine event reactions ----------
  private onEngineEvent(ev: EngineEvent): void {
    if (ev.type === 'spawn') return;
    if (ev.type === 'rotate') {
      // Subtle uniform blur pulse on rotate — adds tactile flicker
      // without obscuring the piece. Kept very low so the edge-bleed
      // (Pixi BlurFilter softens cell edges, which reveals the grid
      // behind flat-style cells) stays imperceptible.
      this.pixi?.playerField?.pulsePieceBlur(1.2, 1.2, 90);
      return;
    }
    if (ev.type === 'hold') return;
    if (ev.type === 'hold_rejected') {
      // Player tried to hold while the slot was already locked — shake
      // the held piece + play the dedicated error sfx (tunable in the
      // audio settings like every other sound).
      this.pixi?.playerField?.shakeHold();
      this.audio.play('error');
      return;
    }
    if (ev.type === 'move') {
      if (!ev.auto) this.audio.play('move');
      return;
    }
    if (ev.type === 'harddrop') {
      this.pixi?.playerField?.shake(Math.min(8, 1 + ev.cells * 0.25), 220);
      // Vertical-biased motion blur on hard drop — strength scales with
      // the fall distance so a longer slam blurs more. Capped low so
      // the BlurFilter's edge softening doesn't visibly bleed the
      // grid through flat-style cells.
      const blurY = Math.min(2.5, 0.8 + ev.cells * 0.1);
      this.pixi?.playerField?.pulsePieceBlur(0, blurY, 130);
      return;
    }
    if (ev.type === 'lock') {
      this.bestComboThisRun = Math.max(this.bestComboThisRun, ev.result.comboCount);
      const eng = this.activeEngine();
      if (eng) this.bestB2BThisRun = Math.max(this.bestB2BThisRun, Math.max(0, eng.stats.backToBack));
      this.checkAchievements({
        cleared: ev.result.cleared.length,
        tSpinKind: ev.result.tSpinKind,
        isPerfectClear: ev.result.isPerfectClear,
        isBackToBack: ev.result.isBackToBack,
        comboCount: ev.result.comboCount,
      });
      this.audio.play('lock');
      const pf = this.pixi?.playerField;
      if (ev.result.cleared.length > 0) {
        const name = ev.result.cleared.length === 1 ? 'clear1'
                   : ev.result.cleared.length === 2 ? 'clear2'
                   : ev.result.cleared.length === 3 ? 'clear3'
                   : 'clear4';
        this.audio.play(name);
        if (ev.result.tSpinKind !== 'none') this.audio.play('tspin');
        if (ev.result.isPerfectClear) this.audio.play('pc');
        if (ev.result.isBackToBack) this.audio.play('b2b');
        if (ev.result.comboCount >= 1) this.audio.play('combo', ev.result.comboCount);
        let shake = 2 + ev.result.cleared.length * 1.5;
        if (ev.result.tSpinKind === 'full') shake += 3;
        if (ev.result.isPerfectClear) shake += 6;
        if (ev.result.isBackToBack) shake += 2;
        pf?.shake(shake, 280);
        pf?.flashRows(ev.result.cleared);
        // Accolade text — anchored to the piece that triggered the clear so
        // it pops up where the player's eye already is.
        const palette = getPalette(this.settings.paletteId, this.settings.customPalettes);
        const shape = shapeOf(ev.piece.type, ev.piece.rotation);
        // Average column + topmost visible row of the locked piece.
        let sumX = 0;
        let minY = 999;
        for (const [dx, dy] of shape) {
          sumX += ev.piece.x + dx;
          const visY = ev.piece.y + dy - BOARD_BUFFER_HEIGHT;
          if (visY < minY) minY = visY;
        }
        const col = sumX / shape.length - 0.5;
        const row = Math.max(0, minY);
        // Accolade sizes scale to cellPx so they don't dominate small boards.
        const cellPx = pf?.options.cellPx ?? 32;
        const sz = (mul: number) => Math.round(cellPx * mul);
        // Accolade text — more specific for T-spin variants so the player
        // sees exactly what kind of clear they just landed. Order matters:
        // primary clear type first (largest), modifiers (B2B, combo) after.
        const cleared = ev.result.cleared.length;
        const isFullT = ev.result.tSpinKind === 'full';
        const isMiniT = ev.result.tSpinKind === 'mini';
        // Push clear events into the sidebar feed. Colors are picked from
        // the active palette so the feed feels rewarding + theme-tinted:
        // T-spins use the T-piece color, tetrises use I, perfect clear
        // uses accent2 (the headline rare-event color), combos use
        // accent. Low-value clears (single / double) use dim text so the
        // rare big plays visually pop above the routine ones.
        void sz; void col; void row;
        const push = (text: string, color: string) => pf?.pushFeedEntry(text, color);
        if (isFullT && cleared === 1) push('t-spin single', palette.pieces.T);
        else if (isFullT && cleared === 2) push('t-spin double', palette.pieces.T);
        else if (isFullT && cleared === 3) push('t-spin triple', palette.pieces.T);
        else if (isFullT) push('t-spin', palette.pieces.T);
        else if (isMiniT && cleared >= 1) push(cleared === 2 ? 'mini t-double' : 'mini t-spin', palette.pieces.T);
        else if (cleared === 4) push('tetris', palette.pieces.I);
        else if (cleared === 3) push('triple', palette.pieces.O);
        else if (cleared === 2) push('double', palette.pieces.S);
        else if (cleared === 1) push('single', palette.textDim);
        if (ev.result.isPerfectClear) push('all clear', palette.accent2);
        if (ev.result.isBackToBack && (cleared === 4 || isFullT)) {
          const engForB2B = this.activeEngine();
          const b2bChain = Math.max(1, engForB2B?.stats.backToBack ?? 1);
          push(`b2b x${b2bChain}`, palette.accent);
        }
        if (ev.result.comboCount >= 2) {
          const cn = ev.result.comboCount + 1;
          push(`combo x${cn}`, palette.accent2);
        }
      }
      return;
    }
    if (ev.type === 'topout') {
      this.audio.play('topout');
      this.pixi?.playerField?.shake(18, 900);
      this.showEndOverlay('topped out', 'topout');
      this.checkAchievements(null);
      // Results panel fades in beneath the rising overlay text — endRun
      // is fired well before the overlay finishes its float-up so the
      // two animations overlap cleanly.
      setTimeout(() => this.endRun('topout'), 1100);
      return;
    }
    if (ev.type === 'finish') {
      this.audio.play('pc');
      const modeId = this.currentMode?.id;
      const finishText = modeId === 'sprint40' ? '40 lines clear'
                       : modeId === 'marathon' ? 'marathon complete'
                       : modeId === 'cheese' ? 'garbage clear'
                       : modeId === 'ultra2min' ? 'time up'
                       : 'complete';
      this.showEndOverlay(finishText, 'finish');
      this.pixi?.playerField?.shake(10, 500);
      this.checkAchievements(null);
      setTimeout(() => this.endRun('finish'), 1100);
      return;
    }
    if (ev.type === 'levelup') {
      this.audio.play('levelup');
      const pf = this.pixi?.playerField;
      // Push into the sidebar feed so the level bump reads alongside
      // the rest of the gameplay events instead of as a floating accolade
      // that the user can miss off the bottom of the screen.
      pf?.pushFeedEntry(`level ${ev.level}`, '#ffd23f');
      pf?.shake(4, 240);
      pf?.flashBoardRim('#ffd23f', 480);
      // Anchor the popup just BELOW the player's board. Above is now
      // the achievement toast's home, and if both fire on the same
      // lock (rare but possible) the level popup would otherwise sit
      // right on top of the unlock card. Below keeps both in the
      // player's eye line without collision.
      const rect = this.pixi?.playerBoardViewportRect() ?? null;
      playfieldToast(`level ${ev.level}`, rect ?? undefined, 'below');
      this.audio.setMusicLevel(ev.level);
    }
  }

  private checkAchievements(
    lastClear: { cleared: number; tSpinKind: 'none' | 'mini' | 'full'; isPerfectClear: boolean; isBackToBack: boolean; comboCount: number } | null,
  ): void {
    const eng = this.activeEngine();
    if (!eng || !this.currentMode) return;
    const unlocked = new Set(this.stats.achievements);
    // For the "completed every mode" achievement, fold the current run into
    // modeRuns when the engine has just finished — that way the unlock fires
    // on the last mode rather than waiting until the next run starts.
    const runCounts = { ...this.stats.modeRuns };
    if (eng.state === 'finished') {
      const id = this.currentMode.id;
      runCounts[id] = (runCounts[id] ?? 0) + 1;
    }
    const nu = newlyUnlocked(unlocked, {
      engine: eng,
      modeId: this.currentMode.id,
      totals: {
        lines:         this.stats.totalLines         + eng.stats.lines,
        pieces:        this.stats.totalPieces        + eng.stats.pieces,
        tetrises:      this.stats.totalTetrises      + eng.stats.tetrises,
        tspins:        this.stats.totalTspins        + eng.stats.tspins,
        tspinsFull:    this.stats.totalTspinsFull    + eng.stats.tspinsFull,
        tspinsMini:    this.stats.totalTspinsMini    + eng.stats.tspinsMini,
        tspinSingles:  this.stats.totalTspinSingles  + eng.stats.tspinSingles,
        tspinDoubles:  this.stats.totalTspinDoubles  + eng.stats.tspinDoubles,
        tspinTriples:  this.stats.totalTspinTriples  + eng.stats.tspinTriples,
        perfectClears: this.stats.totalPerfectClears + eng.stats.perfectClears,
        hardDrops:     this.stats.totalHardDrops     + eng.stats.hardDrops,
        runs:          this.stats.totalRuns,
        playtimeMs:    this.stats.totalPlaytimeMs    + eng.stats.elapsedMs,
        modeRuns:      runCounts,
      },
      lastClear,
      bestComboThisRun: this.bestComboThisRun,
      bestB2BThisRun: this.bestB2BThisRun,
    });
    for (const a of nu) {
      this.stats.achievements.push(a.id);
      this.queueAchievementToast(a);
    }
    if (nu.length) saveStats(this.stats);
  }

  /** Achievement toasts queue strictly — one shows at a time, the next
   *  starts only after the previous has finished its full enter/hold/
   *  exit cycle. Prevents stacking + sfx collisions when a single lock
   *  unlocks multiple achievements at once. */
  private achievementQueue: Achievement[] = [];
  private achievementShowing = false;
  private queueAchievementToast(a: Achievement): void {
    this.achievementQueue.push(a);
    this.drainAchievementQueue();
  }
  private drainAchievementQueue(): void {
    if (this.achievementShowing) return;
    const a = this.achievementQueue.shift();
    if (!a) return;
    this.achievementShowing = true;
    this.showAchievementToast(a, () => {
      this.achievementShowing = false;
      this.drainAchievementQueue();
    });
  }

  private showAchievementToast(a: Achievement, onDone: () => void): void {
    this.audio.play('achievement');
    const cat = categoryFor(a.category);
    // Singular form for the toast tag, since the category color already
    // signals which group the achievement belongs to.
    const tag = ({ milestones: 'milestone unlocked', skills: 'skill unlocked',
                   modes: 'mode unlocked', streaks: 'streak unlocked' } as Record<string, string>)[cat.label]
                   ?? 'achievement unlocked';
    const el2 = el('div', { class: `achievement-toast ach-cat-${a.category}` }, [
      el('div', { class: 'ach-icon' }, [icon(cat.icon)]),
      el('div', { class: 'ach-body' }, [
        el('div', { class: 'ach-tag' }, [tag]),
        el('div', { class: 'ach-name' }, [a.name]),
        el('div', { class: 'ach-desc' }, [a.description]),
      ]),
    ]);
    // Anchor just above the player's board. The queue ensures only
    // one is on screen at a time, so no per-toast stacking offset.
    const rect = this.pixi?.playerBoardViewportRect() ?? null;
    if (rect) {
      el2.classList.add('above-board');
      el2.style.left = `${Math.round(rect.left + rect.width / 2)}px`;
      el2.style.top = `${Math.round(rect.top - 12)}px`;
      el2.style.bottom = 'auto';
      document.body.appendChild(el2);
      // The toast grows UPWARD from its anchor (translateY(-100%)).
      // On layouts where the board top hugs the viewport top, that
      // would push the card off-screen — clamp so its top edge keeps
      // at least 8px of breathing room.
      const minAnchor = 8 + el2.offsetHeight;
      if (rect.top - 12 < minAnchor) el2.style.top = `${minAnchor}px`;
    } else {
      el2.style.bottom = `88px`;
      el2.style.top = 'auto';
      el2.style.left = 'auto';
      document.body.appendChild(el2);
    }
    // 4000ms matches achievement-toast animation total (320ms in + 3380ms
    // hold + 300ms out). +50ms grace for animation completion before DOM
    // teardown so the fade-out completes cleanly.
    setTimeout(() => {
      el2.remove();
      onDone();
    }, 4050);
  }
}

function metricFor(modeId: ModeId, stats: GameStats): { label: string; value: string } {
  if (modeId === 'sprint40') return { label: 'time', value: formatTime(stats.elapsedMs) };
  if (modeId === 'ultra2min') return { label: 'time left', value: formatTime(Math.max(0, 120000 - stats.elapsedMs)) };
  if (modeId === 'cheese') return { label: 'time', value: formatTime(stats.elapsedMs) };
  return { label: 'score', value: stats.score.toLocaleString() };
}

// Bootstrap.
new App();

// One-shot cleanup: unregister any stale service worker a player might
// be carrying from a previous deploy (none ship in this build, but a
// leftover SW can intercept fetches and tag the HTML response as
// POST-derived, surfacing the 'Confirm Form Resubmission' prompt on a
// hard refresh). Best-effort; we never await it.
if ('serviceWorker' in navigator) {
  navigator.serviceWorker.getRegistrations()
    .then((regs) => { for (const r of regs) { try { r.unregister(); } catch { /* */ } } })
    .catch(() => { /* private-mode browsers reject; ignore */ });
}

// Anchor unused exports so tree-shaking keeps the modes / achievements registries.
void ALL_MODES.length; void ACHIEVEMENTS.length; void BOARD_WIDTH;
